<template>
  <v-form v-model="valid">
    <v-container fluid class="mt-4">
      <v-row dense>
        <v-col cols="12" sm="6" md="5">
          <v-text-field label="Name" v-model="item.name" :rules="[fieldRules.required,fieldRules.maxLength(100)]" :counter="100" outlined dense/>
        </v-col>
        <v-col cols="6" sm="3" md="2">
          <v-text-field label="SKU" v-model="item.sku" :rules="[fieldRules.maxLength(20)]" :counter="20" outlined dense/>
        </v-col>
        <v-col cols="2" lg="1">
          <v-checkbox label="Active" v-model="item.active" dense class="mt-1"/>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="6" md="3">
          <v-autocomplete v-model="item.type" outlined dense label="Type" :disabled="item.id!=undefined"
                          :items="[{text:'Item',value:'Item',disabled:disallowItem!=null},{text:'Item (not tracked)',value:'NonTracking'},{text:'Service',value:'Service'}]"
                          @change="changeType"
                          :hint="itemHint" persistent-hint/>
        </v-col>
        <v-col cols="6" md="3">
          <v-menu :disabled="!item.id" max-width="300px" offset-y v-model="lotTrackingWarning" :open-on-click="false">
            <template v-slot:activator="{ on }">
              <v-checkbox label="Enable lot tracking" v-model="item.lotTracking" dense class="mt-0" :disabled="item.type!=='Item' || item.allowNegativeInventory"
                    :hint="item.type!=='Item'?'n/a':(item.allowNegativeInventory ? 'Not available if negative inventory allowed' : '')" persistent-hint
                    @click="lotTrackingWarning=!item.lotTracking"/>
            </template>
            <v-card class="pa-4 warning">
              Turning off lot tracking will permanently remove all lot codes from historical transactions.
            </v-card>
          </v-menu>
        </v-col>
        <v-col class="text-caption mt-1" v-if="disallowItem!=null">
          {{disallowItem}}
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="6" sm="3" md="2">
          <v-text-field label="Tracking units" v-model="item.trackUnits" :rules="[fieldRules.maxLength(10)]" :counter="10" outlined dense @change="item.buyingUnits=item.trackUnits"/>
        </v-col>
        <v-col cols="6" sm="3" md="2">
          <v-select label="Decimal places" v-model="item.precision" :items="[0,1,2,3]" outlined dense hint="for quantities" @change="item.buyingPrecision=item.precision"/>
        </v-col>
        <v-col cols="8" sm="6" md="4" lg="3" v-if="item.type==='Item'">
          <v-checkbox label="Allow negative inventory" v-model="item.allowNegativeInventory" dense class="mt-1"
                      :disabled="item.lotTracking"
                      :hint="item.lotTracking ? 'Cannot allow if lot tracking enabled':''"
                      persistent-hint/>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" sm="6" md="4">
          <inventory-categories-select v-model="item.categoryId" label="Category" type="Item" allow-add hint/>
        </v-col>
      </v-row>

      <v-row v-if="item.type==='Item' && values && connectedToAccounting">
        <v-col cols="12" sm="6" md="4">
          <account-picker v-model="item.inventoryAccountRef" label="Inventory asset account"
                          :accounts="inventoryAccounts" :addTypes="['OtherCurrentAsset','FixedAsset','OtherAsset']"
                          hint="The inventory account used to track the value of this item"/>
        </v-col>
      </v-row>

      <v-row v-else-if="item.type!=='Item'">
        <v-col cols="6" sm="3" md="2">
          <decimal-number-input dense v-model="item.usageCost" :precision="4" label="Usage cost per unit"/>
        </v-col>
        <v-col cols="12" sm="6" md="4" v-if="values && connectedToAccounting" >
          <account-picker v-model="item.inventoryAccountRef" label="Usage expense account"
                          :accounts="expenseAccounts" :addTypes="['Expense']"
                          hint="The account used to track production usage of this item"/>
        </v-col>
      </v-row>

      <template v-if="item.type!=='Service'">
        <v-row>
          <v-col cols="6" sm="3" md="2">
            <v-text-field label="Location" v-model="item.location" :rules="[fieldRules.maxLength(20)]" :counter="20" outlined dense
                  hint="e.g. aisle, bin"/>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="6" sm="3" md="2">
            <v-text-field label="UPC" v-model="item.upc" :counter="12" :rules="[upc]" outlined dense/>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="6" sm="3" md="2">
            <v-checkbox label="Assembled" v-model="item.assembled" dense/>
          </v-col>
        </v-row>
      </template>
    </v-container>

  </v-form>
</template>

<script>
import InventoryCategoriesSelect from '@/views/inventory/categories/InventoryCategoriesSelect'
import barcode from '@/mixins/barcode'

export default {
  name: 'InventoryItemsEditInfo',
  mixins: [barcode],
  components: {InventoryCategoriesSelect},
  props: {
    item: {type:Object, required:true},
    accounts: {type:Array, required:true},
    companyAccounts: {type:Object, required:true},
    disallowItem: {type:String, required:false},
    values: {type:Boolean, required:true}
  },
  data() {
    return {
      valid: true,
      lotTrackingWarning: false
    }
  },
  computed: {
    connectedToAccounting() {
      return this.$store.getters.company.connectedToAccounting;
    },
    inventoryAccounts() {
      return this.accounts.filter(account => ['OtherCurrentAsset','FixedAsset','OtherAsset'].includes(account.type));
    },
    expenseAccounts() {
      return this.accounts.filter(account => account.type === 'Expense');
    },
    itemHint() {
      if (this.item.type === 'Item')
        return 'Quantities are tracked';
      if (this.item.type === 'NonTracking')
        return 'Quantities are not tracked';
      return 'Not tracked';
    }
  },
  watch: {
    'item.lotTracking'() {
      if (this.item.lotTracking)
        this.item.allowNegativeInventory = false;
    }
  },
  methods: {
    changeType() {
      this.item.lotTracking = this.item.type === 'Item';

      if (this.item.type === 'Item') {
        this.item.inventoryAccountRef = this.companyAccounts.itemInventory;
        this.item.incomeAccountRef = this.companyAccounts.itemIncome;
        this.item.cogAccountRef = this.companyAccounts.itemCOG;
      }
      else if (this.item.type === 'NonTracking') {
        this.item.inventoryAccountRef = this.companyAccounts.nonInventoryUsage;
        this.item.incomeAccountRef = this.companyAccounts.nonInventoryIncome;
        this.item.cogAccountRef = this.companyAccounts.nonInventoryExpense;
      }
      else if (this.item.type === 'Service') {
        this.item.inventoryAccountRef = this.companyAccounts.serviceUsage;
        this.item.incomeAccountRef = this.companyAccounts.serviceIncome;
        this.item.cogAccountRef = this.companyAccounts.serviceExpense;
      }
    }
  }
}
</script>